import { ChangeDetectionStrategy, Component, computed, Resource, signal } from '@angular/core';
import { EmptyStateComponent, TranslatePipe } from '@softline/ui-core';
import type { ILoadingOverlayParams } from 'ag-grid-enterprise';
import { ResourceContainerComponent } from '@softapps/core';

type NoRowsParams = ILoadingOverlayParams<any, any> & {
  dataSource?: Resource<any>;
  onReload?: () => void;
};

@Component({
  selector: 'soft-grid-no-rows',
  imports: [ResourceContainerComponent, EmptyStateComponent, TranslatePipe],
  template: `
    @if (loadingOrFailed() && resource(); as res) {
      <soft-resource-container [resource]="res"></soft-resource-container>
    } @else {
      <soft-ds-empty-state
        variant="empty"
        actionSize="md"
        [title]="('#SOFTAPPS_CORE.COMPONENTS.GRID.EMPTY_TITLE' | translate) ?? null"
        [description]="
          ('#SOFTAPPS_CORE.COMPONENTS.GRID.EMPTY_DESCRIPTION' | translate) ?? null
        "
        [actionLabel]="
          canReload() ? (('#SOFTAPPS_CORE.COMPONENTS.GRID.RELOAD' | translate) ?? null) : null
        "
        (action)="reload()"
      />
    }
  `,
  styles: `
    :host {
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: auto;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridNoRowsOverlay {
  readonly resource = signal<Resource<any> | undefined>(undefined);
  readonly canReload = signal(false);

  /** the resource container owns loading and failure; a resolved-but-empty resource gets the
      same empty state (and the same retry) as any other source */
  protected readonly loadingOrFailed = computed(() => {
    const resource = this.resource();
    return !!resource && (resource.isLoading() || !!resource.error());
  });

  private onReload: (() => void) | null = null;

  agInit(params: NoRowsParams): void {
    this.setParams(params);
  }
  refresh?(params: NoRowsParams): void {
    this.setParams(params);
  }

  private setParams(params: NoRowsParams): void {
    this.resource.set(params.dataSource);
    this.onReload = params.onReload ?? null;
    this.canReload.set(!!params.onReload);
  }

  protected reload(): void {
    this.onReload?.();
  }
}
